/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { jsonWebKeySetsActivateKey } from "../funcs/jsonWebKeySetsActivateKey.js";
import { jsonWebKeySetsCreate } from "../funcs/jsonWebKeySetsCreate.js";
import { jsonWebKeySetsDelete } from "../funcs/jsonWebKeySetsDelete.js";
import { jsonWebKeySetsGet } from "../funcs/jsonWebKeySetsGet.js";
import { jsonWebKeySetsGetDeletePreflight } from "../funcs/jsonWebKeySetsGetDeletePreflight.js";
import { jsonWebKeySetsList } from "../funcs/jsonWebKeySetsList.js";
import { jsonWebKeySetsListKeys } from "../funcs/jsonWebKeySetsListKeys.js";
import { jsonWebKeySetsPublishKey } from "../funcs/jsonWebKeySetsPublishKey.js";
import { jsonWebKeySetsRetireKey } from "../funcs/jsonWebKeySetsRetireKey.js";
import { jsonWebKeySetsRevokeKey } from "../funcs/jsonWebKeySetsRevokeKey.js";
import { jsonWebKeySetsUpdate } from "../funcs/jsonWebKeySetsUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { JSONWebKey } from "../models/components/jsonwebkey.js";
import { JSONWebKeySet } from "../models/components/jsonwebkeyset.js";
import { JSONWebKeySetDeletePreflight } from "../models/components/jsonwebkeysetdeletepreflight.js";
import { ListJSONWebKeySetsResult } from "../models/components/listjsonwebkeysetsresult.js";
import { ListJSONWebKeysResult } from "../models/components/listjsonwebkeysresult.js";
import {
  ActivateJsonWebKeyRequest,
  ActivateJsonWebKeySecurity,
} from "../models/operations/activatejsonwebkey.js";
import {
  CreateJsonWebKeySetRequest,
  CreateJsonWebKeySetSecurity,
} from "../models/operations/createjsonwebkeyset.js";
import {
  DeleteJsonWebKeySetRequest,
  DeleteJsonWebKeySetSecurity,
} from "../models/operations/deletejsonwebkeyset.js";
import {
  GetJsonWebKeySetRequest,
  GetJsonWebKeySetSecurity,
} from "../models/operations/getjsonwebkeyset.js";
import {
  GetJsonWebKeySetDeletePreflightRequest,
  GetJsonWebKeySetDeletePreflightSecurity,
} from "../models/operations/getjsonwebkeysetdeletepreflight.js";
import {
  ListJsonWebKeysRequest,
  ListJsonWebKeysSecurity,
} from "../models/operations/listjsonwebkeys.js";
import {
  ListJsonWebKeySetsRequest,
  ListJsonWebKeySetsSecurity,
} from "../models/operations/listjsonwebkeysets.js";
import {
  PublishJsonWebKeyRequest,
  PublishJsonWebKeySecurity,
} from "../models/operations/publishjsonwebkey.js";
import {
  RetireJsonWebKeyRequest,
  RetireJsonWebKeySecurity,
} from "../models/operations/retirejsonwebkey.js";
import {
  RevokeJsonWebKeyRequest,
  RevokeJsonWebKeySecurity,
} from "../models/operations/revokejsonwebkey.js";
import {
  UpdateJsonWebKeySetRequest,
  UpdateJsonWebKeySetSecurity,
} from "../models/operations/updatejsonwebkeyset.js";
import { unwrapAsync } from "../types/fp.js";

export class JsonWebKeySets extends ClientSDK {
  /**
   * activateKey jsonWebKeySets
   *
   * @remarks
   * Make a published key the set's active signing key, retiring the previously active key in the same operation. The key must be pending or retired; activating the already-active key is a no-op. Requires org:admin.
   */
  async activateKey(
    request: ActivateJsonWebKeyRequest,
    security?: ActivateJsonWebKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKey> {
    return unwrapAsync(jsonWebKeySetsActivateKey(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createSet jsonWebKeySets
   *
   * @remarks
   * Create a JSON Web Key Set backed by an organization external key, minting and publishing the set's first key straight to active. Reads the backing key's public half from the customer's KMS and refuses when the key's real algorithm disagrees with the one recorded against it. Rate limited per organization. Requires org:admin.
   */
  async create(
    request: CreateJsonWebKeySetRequest,
    security?: CreateJsonWebKeySetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKeySet> {
    return unwrapAsync(jsonWebKeySetsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteSet jsonWebKeySets
   *
   * @remarks
   * Soft-delete a JSON Web Key Set by ID, withdrawing every key still published in it in the same operation. Refused with a conflict while any live remote_session_client still references the set — detach it there first; see getDeletePreflight. Requires org:admin. Tokens signed with the set's keys stop verifying, so treat this as decommissioning the set's whole trust anchor rather than tidying up.
   */
  async delete(
    request: DeleteJsonWebKeySetRequest,
    security?: DeleteJsonWebKeySetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(jsonWebKeySetsDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getSet jsonWebKeySets
   *
   * @remarks
   * Get a JSON Web Key Set by ID. Requires org:read.
   */
  async get(
    request: GetJsonWebKeySetRequest,
    security?: GetJsonWebKeySetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKeySet> {
    return unwrapAsync(jsonWebKeySetsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getSetDeletePreflight jsonWebKeySets
   *
   * @remarks
   * Authoritative impact summary for deleting a JSON Web Key Set: the remote_session_clients still referencing it. deleteSet returns a conflict for exactly the sets this reports a non-zero client_count for. Requires org:read.
   */
  async getDeletePreflight(
    request: GetJsonWebKeySetDeletePreflightRequest,
    security?: GetJsonWebKeySetDeletePreflightSecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKeySetDeletePreflight> {
    return unwrapAsync(jsonWebKeySetsGetDeletePreflight(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSets jsonWebKeySets
   *
   * @remarks
   * List the organization's JSON Web Key Sets. Requires org:read.
   */
  async list(
    request?: ListJsonWebKeySetsRequest | undefined,
    security?: ListJsonWebKeySetsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListJSONWebKeySetsResult> {
    return unwrapAsync(jsonWebKeySetsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listKeys jsonWebKeySets
   *
   * @remarks
   * List a JSON Web Key Set's published keys, newest first. Revoked keys drop out of the default listing; pass include_revoked to see the set's full revocation history. Requires org:read.
   */
  async listKeys(
    request: ListJsonWebKeysRequest,
    security?: ListJsonWebKeysSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListJSONWebKeysResult> {
    return unwrapAsync(jsonWebKeySetsListKeys(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * publishKey jsonWebKeySets
   *
   * @remarks
   * Mint and publish a new key from the set's current backing external key. The key is published as pending — visible to verifiers so their caches warm up — unless the set has no active key, in which case it activates immediately. Publishing the same backing key again while its kid is present in the set (including revoked) is refused as a conflict. Reads the public half from the customer's KMS; rate limited per organization. Requires org:admin.
   */
  async publishKey(
    request: PublishJsonWebKeyRequest,
    security?: PublishJsonWebKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKey> {
    return unwrapAsync(jsonWebKeySetsPublishKey(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * retireKey jsonWebKeySets
   *
   * @remarks
   * Take the set's active key out of signing use without withdrawing it: the key stays published so tokens already signed with it keep verifying. The key must be active. This is the graceful wind-down; use revoke when the key must stop verifying too. Requires org:admin.
   */
  async retireKey(
    request: RetireJsonWebKeyRequest,
    security?: RetireJsonWebKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKey> {
    return unwrapAsync(jsonWebKeySetsRetireKey(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * revokeKey jsonWebKeySets
   *
   * @remarks
   * Withdraw a published key entirely: it leaves the published set and tokens signed with it stop verifying. This is the compromise response, not the graceful wind-down — use retire for that. A revoked kid can never be republished into the set. Requires org:admin.
   */
  async revokeKey(
    request: RevokeJsonWebKeyRequest,
    security?: RevokeJsonWebKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKey> {
    return unwrapAsync(jsonWebKeySetsRevokeKey(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateSet jsonWebKeySets
   *
   * @remarks
   * Update a JSON Web Key Set's name and backing external key. Requires org:admin. Both fields are replaced, not patched. Re-pointing the backing key is how rotation begins: point the set at the new external key, then publish a key from it. Already-published keys are unaffected — each keeps signing with the external key it was minted from.
   */
  async update(
    request: UpdateJsonWebKeySetRequest,
    security?: UpdateJsonWebKeySetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<JSONWebKeySet> {
    return unwrapAsync(jsonWebKeySetsUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
